import type { ReactNode } from 'react';
import {
  DeleteSweep as DiscardIcon,
  Inventory2 as InventoryIcon,
  AssignmentReturn as SupplyReturnIcon,
  LocalShipping as SupplyIcon,
  MenuBook as BomIcon,
  Paid as SupplierPaymentIcon,
  Payment as PaymentIcon,
  PrecisionManufacturing as ProductionIcon,
  RestoreFromTrash as RestoreIcon,
  ShoppingCart as SaleIcon,
  SwapHoriz as MoveIcon,
  Transform as ConvertIcon,
  AccountBalanceWallet as WalletIcon,
  Payments as ExpenseIcon,
  ConfirmationNumber as VoucherIcon,
  Savings as DepositIcon,
} from '@mui/icons-material';
import type { RecentTransactionType } from './DashboardContext';

export type RecentTxChipColor = 'success' | 'info' | 'warning' | 'error' | 'secondary';

export type RecentTransactionStyle = {
  color: string;
  chipColor: RecentTxChipColor;
  icon: ReactNode;
};

export const RECENT_TX_TYPE_ORDER: RecentTransactionType[] = [
  'sale',
  'payment',
  'supply',
  'supply_return',
  'supply_payment',
  'movement',
  'discard',
  'restore',
  'inventory',
  'conversion',
  'production',
  'bom',
  'wallet',
  'expense',
  'voucher',
  'deposit',
];

const LIGHT_COLORS: Record<RecentTransactionType, string> = {
  sale: '#2e7d32',
  supply: '#0277bd',
  supply_return: '#ef6c00',
  movement: '#ed6c02',
  discard: '#c62828',
  restore: '#2e7d32',
  inventory: '#0277bd',
  conversion: '#7b1fa2',
  payment: '#00695c',
  supply_payment: '#6d4c41',
  production: '#1565c0',
  bom: '#6a1b9a',
  wallet: '#1565c0',
  expense: '#6d4c41',
  voucher: '#c62828',
  deposit: '#ed6c02',
};

const DARK_COLORS: Record<RecentTransactionType, string> = {
  sale: '#81c784',
  supply: '#4fc3f7',
  supply_return: '#ffb74d',
  movement: '#ffcc80',
  discard: '#ef9a9a',
  restore: '#a5d6a7',
  inventory: '#4dd0e1',
  conversion: '#ce93d8',
  payment: '#80cbc4',
  supply_payment: '#bcaaa4',
  production: '#64b5f6',
  bom: '#b39ddb',
  wallet: '#90caf9',
  expense: '#bcaaa4',
  voucher: '#ef9a9a',
  deposit: '#ffcc80',
};

const CHIP_COLOR: Record<RecentTransactionType, RecentTxChipColor> = {
  sale: 'success',
  supply: 'info',
  supply_return: 'warning',
  movement: 'warning',
  discard: 'error',
  restore: 'success',
  inventory: 'info',
  conversion: 'secondary',
  payment: 'success',
  supply_payment: 'warning',
  production: 'info',
  bom: 'secondary',
  wallet: 'info',
  expense: 'warning',
  voucher: 'error',
  deposit: 'warning',
};

const TYPE_ICONS: Record<RecentTransactionType, ReactNode> = {
  sale: <SaleIcon sx={{ fontSize: 16 }} />,
  supply: <SupplyIcon sx={{ fontSize: 16 }} />,
  supply_return: <SupplyReturnIcon sx={{ fontSize: 16 }} />,
  movement: <MoveIcon sx={{ fontSize: 16 }} />,
  discard: <DiscardIcon sx={{ fontSize: 16 }} />,
  restore: <RestoreIcon sx={{ fontSize: 16 }} />,
  inventory: <InventoryIcon sx={{ fontSize: 16 }} />,
  conversion: <ConvertIcon sx={{ fontSize: 16 }} />,
  payment: <PaymentIcon sx={{ fontSize: 16 }} />,
  supply_payment: <SupplierPaymentIcon sx={{ fontSize: 16 }} />,
  production: <ProductionIcon sx={{ fontSize: 16 }} />,
  bom: <BomIcon sx={{ fontSize: 16 }} />,
  wallet: <WalletIcon sx={{ fontSize: 16 }} />,
  expense: <ExpenseIcon sx={{ fontSize: 16 }} />,
  voucher: <VoucherIcon sx={{ fontSize: 16 }} />,
  deposit: <DepositIcon sx={{ fontSize: 16 }} />,
};

export const recentTransactionStyle: Record<RecentTransactionType, RecentTransactionStyle> =
  Object.fromEntries(
    RECENT_TX_TYPE_ORDER.map((type) => [
      type,
      { icon: TYPE_ICONS[type], color: LIGHT_COLORS[type], chipColor: CHIP_COLOR[type] },
    ]),
  ) as Record<RecentTransactionType, RecentTransactionStyle>;

export const styleForRecentTransaction = (
  type: string,
  mode: 'light' | 'dark' = 'light',
): RecentTransactionStyle => {
  const key = (type in LIGHT_COLORS ? type : 'sale') as RecentTransactionType;
  const colors = mode === 'dark' ? DARK_COLORS : LIGHT_COLORS;
  return {
    icon: TYPE_ICONS[key],
    color: colors[key],
    chipColor: CHIP_COLOR[key],
  };
};
